import React, { useCallback } from 'react';
import { Spinner } from '@heroui/react';
import { Typography, UniversalTable } from '../../../../components';
import { useTranslations } from '../../../../contexts/translation.context';
import { EmptyContentClientIc } from '../../../../components/icons';
import { OrdersTableProps, OrderData } from '../types';
import { getTableColumns } from '../constants';
import { OrderIdCell, AccountCell, OfferCell, CampaignCell, StatusCell, DateCell } from './index';

const OrdersTable: React.FC<OrdersTableProps> = ({
  orders,
  isLoading,
  totalCount,
  currentPage,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onEditOrder,
  onOrderAction,
}) => {
  const { t } = useTranslations();
  const renderCell = useCallback(
    (order: OrderData, columnKey: string) => {
      switch (columnKey) {
        case 'id':
          return <OrderIdCell order={order} onEditOrder={onEditOrder} />;
        case 'account':
          return <AccountCell order={order} />;
        case 'offer':
          return <OfferCell order={order} />;
        case 'campaign':
          return <CampaignCell order={order} />;
        case 'status':
          return <StatusCell order={order} onOrderAction={onOrderAction} />;
        case 'startDate':
          return <DateCell order={order} />;
        default:
          return null;
      }
    },
    [onEditOrder, onOrderAction]
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <UniversalTable
      data={orders}
      columns={getTableColumns(t)}
      renderCell={renderCell}
      emptyContent={
        <div className="text-center py-12">
          <div className="flex flex-col items-center gap-4">
            <EmptyContentClientIc />
            <div className="flex flex-col gap-1.5">
              <Typography variant="h6" className="text-xl font-semibold leading-7">
                {t('orders.table.empty.title')}
              </Typography>
              <Typography
                className="text-neutral-500 text-base font-medium leading-6"
                variant="body1"
              >
                {t('orders.table.empty.description')}
              </Typography>
            </div>
          </div>
        </div>
      }
      pagination={{
        count: totalCount,
        page_size: pageSize,
        page: currentPage,
      }}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      currentPage={currentPage}
      currentPageSize={pageSize}
    />
  );
};

export default OrdersTable;
